import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { fetchTickmillCompaniesAction } from '@/store/identity/identity.actions'
import { IdentityApiClient } from '@/utils/api/identityApi'
import {
  ReferralMaterialsCategoriesDto,
  UpdateReferralMaterialCategoryDto,
} from 'api/ClientArea/ReferralMaterialsDto'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { getNotificationBaseUrlTypes } from 'store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { ReferralMaterialsCategoryDetailsForm } from './referralMaterialsCategoriesDetailsForm'

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  baseUrlTypes: useAppSelector((state) => state.clientArea.baseUrlTypes),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  return {
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchBaseUrlTypes: async () => dispatch(getNotificationBaseUrlTypes(clientAreaApiClient)),
  }
}

const useReferralMaterialsCategoryFetch = () => {
  const { languages, tickmillCompanies, baseUrlTypes } = useMapStateToProps()
  const { fetchClientLanguages, fetchBaseUrlTypes, fetchTickmillCompanies } =
    useMapDispatchToProps()

  const { data, referralMaterialsCategoryData, forceRefresh, isLoading } =
    useCategorySettingsFetch()

  useEffect(() => {
    if (isEmpty(languages)) {
      fetchClientLanguages()
    }
    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompanies()
    }
    if (isEmpty(baseUrlTypes)) {
      fetchBaseUrlTypes()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data,
    referralMaterialsCategoryData,
    forceRefresh,
    isLoading,
    baseUrlTypes,
    languages,
    tickmillCompanies,
  }
}

const useCategorySettingsFetch = () => {
  const { referralMaterialsCategoryId } = useReferralMaterialsCategorySettingsRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [referralMaterialsCategoryData, setReferralMaterialsCategory] =
    useState<ReferralMaterialsCategoriesDto>()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!referralMaterialsCategoryId) return
    const result = await clientAreaApiClient.getReferralMaterialsCategorySettingsById(
      referralMaterialsCategoryId
    )
    if (result) {
      setReferralMaterialsCategory(result)
    }
    return result
  }, [referralMaterialsCategoryId]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data, isLoading } = useFetchOne(callback)

  return { data, referralMaterialsCategoryData, isLoading, forceRefresh }
}

const useReferralMaterialsCategoryMutate = () => {
  const { referralMaterialsCategoryId } = useReferralMaterialsCategorySettingsRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const handleEditSubmit = async (data: UpdateReferralMaterialCategoryDto) => {
    if (referralMaterialsCategoryId) {
      await clientAreaApiClient.updateReferralMaterialCategory(referralMaterialsCategoryId, data)
      setToast(successToast(`IB Referral Material Page updated successfully.`))
    }
  }

  return { handleEditSubmit }
}

const useReferralMaterialsCategorySettingsRoute = () => {
  const params = useParams<{ id: string }>()
  const referralMaterialsCategoryId = params.id

  return { referralMaterialsCategoryId }
}

const ReferralMaterialsCategoryDetailsPage = () => {
  const crumbs = [
    {
      title: 'Marketing - Referral Materials Category Settings',
      link: links.referralMaterialsCategories.url,
    },
  ]

  const referralMaterialsCategoryFetch = useReferralMaterialsCategoryFetch()
  const {
    data,
    referralMaterialsCategoryData,
    isLoading,
    forceRefresh,
    languages,
    tickmillCompanies,
    baseUrlTypes,
  } = referralMaterialsCategoryFetch

  const mutate = useReferralMaterialsCategoryMutate()

  const [auditLogVisible, setAuditLogVisible] = useState(false)

  const handleSubmit = async (updateReferralMaterial: UpdateReferralMaterialCategoryDto) => {
    await mutate.handleEditSubmit(updateReferralMaterial)
    forceRefresh()
  }

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          referralMaterialsCategoryId={referralMaterialsCategoryData?.id}
        />
      )}
      {referralMaterialsCategoryData?.name && (
        <Breadcrumbs
          crumbs={crumbs.concat({ title: referralMaterialsCategoryData.name, link: '#' })}
        />
      )}
      {referralMaterialsCategoryData && <NavigationHeader />}
      <Loading isLoading={isLoading}>
        {data && (
          <ReferralMaterialsCategoryDetailsForm
            data={data}
            languages={languages.filter((lang) => lang.clientAreaVisible)}
            tickmillCompanies={tickmillCompanies}
            baseUrlTypes={baseUrlTypes}
            setAuditLogModal={setAuditLogVisible}
            onSubmit={handleSubmit}
          />
        )}
      </Loading>
    </React.Fragment>
  )
}

export default ReferralMaterialsCategoryDetailsPage
